// om-colormap.ts — original (no GPL) colormaps for Open-Meteo fields, ported
// from poc/spike/colormap.mjs.
//
// A colormap is baked into a 256-entry RGBA LUT. Both the GPU (as a 256x1 RGBA
// texture, sampled NEAREST) and any CPU validator use the SAME LUT and the SAME
// index computation, so rendered pixels match the reference bit-for-bit (modulo
// the 8-bit canvas readback).

import { LIGHTNING_LEVELS } from '../lightning-scale.js'

export const LUT_SIZE = 256

export interface ColorStop {
  t: number // normalized position in [0,1]
  c: [number, number, number] // RGB 0..255
  // Optional per-stop alpha 0..255. When omitted on EVERY stop the LUT is fully
  // opaque (alpha=255) — preserving the original behaviour for colormaps that
  // don't want a transparency ramp. When ANY stop sets `a`, alpha is ramped
  // (linearly interpolated, missing endpoints treated as 0/255 — see rampAlpha).
  a?: number // alpha 0..255
}

export interface Colormap {
  stops: ColorStop[]
  // domain min/max in DATA units; value is normalized to t = (v-min)/(max-min).
  domainMin: number
  domainMax: number
}

// Cloud-cover: transparent over clear sky, ramping to near-opaque overcast.
//   t=0   deep blue,  alpha 0    (cloud-free / clear -> fully TRANSPARENT)
//   t=0.5 cyan-ish,   alpha ~120 (partly cloudy -> faint but visible)
//   t=1   white,      alpha 235  (full overcast -> near-opaque)
// The alpha ramp uses a gentle curve (see buildLUT) so thin cloud stays faint.
export const CLOUD_COVER: Colormap = {
  stops: [
    { t: 0.0, c: [10, 30, 90], a: 0 },
    { t: 0.5, c: [40, 140, 200], a: 120 },
    { t: 1.0, c: [245, 245, 255], a: 235 },
  ],
  domainMin: 0,
  domainMax: 100,
}

// --- Per-variable standard colormaps. Domains in DATA units (see the probe of
// real Open-Meteo ranges). Alpha ramps make "nothing happening" transparent
// (no precip / calm wind / clear lightning); fields where every cell is
// meaningful (temperature, heights) stay opaque and rely on the layer opacity.

// Temperature (°C): cold blue → warm red. Opaque.
export const TEMPERATURE: Colormap = {
  stops: [
    { t: 0.0, c: [70, 0, 110] }, // -40
    { t: 0.18, c: [40, 40, 180] }, // -25
    { t: 0.35, c: [70, 150, 230] }, // -10
    { t: 0.47, c: [160, 215, 235] }, // 0
    { t: 0.53, c: [185, 235, 185] }, // 5
    { t: 0.65, c: [240, 240, 120] }, // 15
    { t: 0.77, c: [240, 170, 80] }, // 25
    { t: 0.88, c: [220, 80, 50] }, // 35
    { t: 1.0, c: [140, 20, 20] }, // 45
  ],
  domainMin: -40,
  domainMax: 45,
}

// Precipitation — aligned with the legacy NWM precip layer's RAIN intensity ramp
// and scale (0–2 in/hr ≈ 0–50.8 mm/hr; legend ticks at 0.1/0.5/1/2 in/hr). OM
// `precipitation` is mm of hourly accumulation, numerically the hour-average rate
// in mm/hr, so the same physical intensity maps to the same color as the legacy
// layer. (Legacy also hue-codes precip TYPE — rain/snow/etc. — which OM's single
// precipitation field can't reproduce, so this is the rain ramp for all precip.)
export const PRECIPITATION: Colormap = {
  stops: [
    { t: 0.0, c: [144, 238, 144], a: 0 }, //  0        transparent
    { t: 0.005, c: [144, 238, 144], a: 150 }, //  0.01 in/hr  light green (NWM threshold)
    { t: 0.05, c: [144, 238, 144], a: 200 }, //  0.1  in/hr  light green
    { t: 0.1, c: [76, 175, 80], a: 210 }, //  0.2  in/hr  green
    { t: 0.25, c: [255, 235, 59], a: 215 }, //  0.5  in/hr  yellow
    { t: 0.5, c: [255, 152, 0], a: 225 }, //  1    in/hr  orange
    { t: 0.75, c: [244, 67, 54], a: 235 }, //  1.5  in/hr  red
    { t: 1.0, c: [183, 28, 28], a: 245 }, //  2+   in/hr  dark red
  ],
  domainMin: 0,
  domainMax: 50.8, // 2 in/hr
}

// CAPE (J/kg): transparent low → green → yellow → orange → red.
export const CAPE: Colormap = {
  stops: [
    { t: 0.0, c: [200, 240, 200], a: 0 },
    { t: 0.06, c: [180, 230, 170], a: 80 },
    { t: 0.25, c: [120, 215, 110], a: 160 },
    { t: 0.5, c: [240, 240, 80], a: 200 },
    { t: 0.75, c: [240, 140, 40], a: 220 },
    { t: 1.0, c: [215, 40, 40], a: 240 },
  ],
  domainMin: 0,
  domainMax: 4000,
}

// Lightning potential (J/kg): transparent low → yellow → orange → red.
export const LIGHTNING: Colormap = {
  stops: [
    { t: 0.0, c: [255, 245, 150], a: 0 },
    { t: 0.07, c: [255, 240, 120], a: 130 },
    { t: 0.33, c: [255, 180, 40], a: 200 },
    { t: 0.67, c: [240, 80, 40], a: 230 },
    { t: 1.0, c: [180, 20, 60], a: 245 },
  ],
  domainMin: 0,
  domainMax: 15,
}

// Unified lightning index (0..1) — the worldwide lightning layer's shared scale.
// Values are pre-normalised to 0..1 on the CPU (see om-layer-service valueTransform)
// from HRRR flash density / LPI / WMO weather_code, so the domain is [0,1] and the
// stops come straight from LIGHTNING_LEVELS (single source of truth, shared with the
// NWM renderer). Non-thunderstorm WMO cells arrive as NaN and the shader discards
// them; LTNG/LPI "None" arrives as 0 (alpha 0 → transparent).
export const UNIFIED_LIGHTNING: Colormap = {
  stops: LIGHTNING_LEVELS.map(l => ({ t: l.index, c: l.rgb, a: l.alpha })),
  domainMin: 0,
  domainMax: 1,
}

// Wind speed (m/s): transparent calm → green → yellow → orange → red.
export const WIND: Colormap = {
  stops: [
    { t: 0.0, c: [200, 230, 255], a: 0 },
    { t: 0.12, c: [120, 200, 240], a: 100 },
    { t: 0.25, c: [80, 200, 120], a: 160 },
    { t: 0.5, c: [240, 230, 80], a: 200 },
    { t: 0.75, c: [240, 130, 50], a: 220 },
    { t: 1.0, c: [200, 40, 60], a: 240 },
  ],
  domainMin: 0,
  domainMax: 40,
}
export const WIND_GUSTS: Colormap = { ...WIND, domainMax: 50 }

// Visibility (m): INVERSE — dense fog opaque red, clear sky transparent. Bands
// follow aviation flight categories (LIFR/IFR/MVFR/VFR).
export const VISIBILITY: Colormap = {
  stops: [
    { t: 0.0, c: [180, 40, 40], a: 235 }, // 0 m — dense fog
    { t: 0.08, c: [220, 120, 40], a: 200 }, // ~1600 m LIFR
    { t: 0.25, c: [200, 80, 200], a: 160 }, // ~5000 m IFR
    { t: 0.4, c: [80, 120, 240], a: 110 }, // ~8000 m MVFR
    { t: 0.6, c: [120, 220, 120], a: 45 }, // ~12000 m VFR
    { t: 1.0, c: [200, 255, 200], a: 0 }, // clear
  ],
  domainMin: 0,
  domainMax: 20000,
}

// Height (m) — freezing level / snow line: cool→warm elevation ramp.
export const HEIGHT: Colormap = {
  stops: [
    { t: 0.0, c: [40, 60, 160], a: 205 },
    { t: 0.2, c: [40, 160, 200], a: 205 },
    { t: 0.4, c: [80, 200, 120], a: 205 },
    { t: 0.6, c: [220, 210, 100], a: 205 },
    { t: 0.8, c: [210, 150, 90], a: 205 },
    { t: 1.0, c: [245, 245, 245], a: 215 },
  ],
  domainMin: 0,
  domainMax: 5000,
}

// Smoke (µg/m³): transparent low → tan → dark brown.
export const SMOKE: Colormap = {
  stops: [
    { t: 0.0, c: [200, 190, 150], a: 0 },
    { t: 0.04, c: [200, 185, 145], a: 60 },
    { t: 0.2, c: [180, 150, 100], a: 140 },
    { t: 0.48, c: [130, 90, 60], a: 200 },
    { t: 1.0, c: [70, 50, 40], a: 235 },
  ],
  domainMin: 0,
  domainMax: 250,
}

// Map an OM variable id to its standard colormap (defaults to CLOUD_COVER).
export function colormapForVariable(variable: string): Colormap {
  switch (variable) {
    case 'temperature_2m':
      return TEMPERATURE
    case 'precipitation':
      return PRECIPITATION
    case 'cape':
      return CAPE
    case 'lightning_potential':
      return LIGHTNING
    case 'wind_speed_10m':
      return WIND
    case 'wind_gusts_10m':
      return WIND_GUSTS
    case 'visibility':
      return VISIBILITY
    case 'freezing_level_height':
    case 'snowfall_height':
      return HEIGHT
    case 'mass_density_8m':
      return SMOKE
    default:
      return CLOUD_COVER // cloud_cover, cloud_cover_high/mid/low
  }
}

// Physical valid range per variable; values outside are treated as fill/garbage
// and masked to NaN (transparent). Some fields (e.g. snowfall_height) carry
// non-sentinel fill values far outside any physical range. null = no clamp.
export function validRangeForVariable(variable: string): [number, number] | null {
  switch (variable) {
    case 'snowfall_height':
    case 'freezing_level_height':
      return [-2000, 12000]
    case 'visibility':
      return [0, 100000]
    case 'cape':
      return [0, 20000]
    case 'temperature_2m':
      return [-120, 70]
    default:
      return null
  }
}

function rampRGB(stops: ColorStop[], t: number): [number, number, number] {
  if (t <= stops[0].t) return [...stops[0].c]
  const lastStop = stops[stops.length - 1]
  if (t >= lastStop.t) return [...lastStop.c]
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i]
    const b = stops[i + 1]
    if (t >= a.t && t <= b.t) {
      const f = (t - a.t) / (b.t - a.t)
      return [
        Math.round(a.c[0] + (b.c[0] - a.c[0]) * f),
        Math.round(a.c[1] + (b.c[1] - a.c[1]) * f),
        Math.round(a.c[2] + (b.c[2] - a.c[2]) * f),
      ]
    }
  }
  return [...lastStop.c]
}

// Interpolate the alpha channel at position t in [0,1]. Returns 0..255.
// A stop's alpha defaults to 255 when unset (so an all-opaque colormap stays
// opaque). Endpoints clamp; interior interpolates linearly between neighbours.
function rampAlpha(stops: ColorStop[], t: number): number {
  const av = (s: ColorStop): number => (s.a == null ? 255 : s.a)
  if (t <= stops[0].t) return av(stops[0])
  const lastStop = stops[stops.length - 1]
  if (t >= lastStop.t) return av(lastStop)
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i]
    const b = stops[i + 1]
    if (t >= a.t && t <= b.t) {
      const f = (t - a.t) / (b.t - a.t)
      return Math.round(av(a) + (av(b) - av(a)) * f)
    }
  }
  return av(lastStop)
}

// Build the 256x1 RGBA LUT (Uint8Array length 256*4). RGB is ramped from the
// stops; ALPHA is ramped from the stops' `a` (defaulting to 255 when absent so
// colormaps without a transparency ramp stay fully opaque). The alpha is baked
// straight into the LUT (NOT premultiplied here — the shader premultiplies at
// draw time so the CPU validator can read RGB and A independently).
export function buildLUT(cmap: Colormap = CLOUD_COVER): Uint8Array {
  const lut = new Uint8Array(LUT_SIZE * 4)
  for (let i = 0; i < LUT_SIZE; i++) {
    const t = i / (LUT_SIZE - 1)
    const [r, g, b] = rampRGB(cmap.stops, t)
    lut[i * 4 + 0] = r
    lut[i * 4 + 1] = g
    lut[i * 4 + 2] = b
    lut[i * 4 + 3] = rampAlpha(cmap.stops, t)
  }
  return lut
}

// Map a data VALUE to a LUT index 0..LUT_SIZE-1 over the colormap's domain.
// This exact formula is replicated in the fragment shader (om-custom-layer.ts)
// so CPU and GPU pick the same LUT entry.
export function valueToLutIndex(value: number, cmap: Colormap = CLOUD_COVER): number {
  let t = (value - cmap.domainMin) / (cmap.domainMax - cmap.domainMin)
  if (t < 0) t = 0
  if (t > 1) t = 1
  return Math.round(t * (LUT_SIZE - 1))
}

// CPU reference: value -> [r,g,b] via the baked LUT (NEAREST).
export function valueToRGB(
  value: number,
  lut: Uint8Array,
  cmap: Colormap = CLOUD_COVER
): [number, number, number] {
  const idx = valueToLutIndex(value, cmap)
  return [lut[idx * 4], lut[idx * 4 + 1], lut[idx * 4 + 2]]
}

// CPU reference: value -> [r,g,b,a] (a = LUT alpha 0..255) via the baked LUT.
// Mirrors the shader's NEAREST LUT sample (rgba). Use this to validate the
// transparency ramp; the shader then premultiplies (rgb*a, a) at draw time.
export function valueToRGBA(
  value: number,
  lut: Uint8Array,
  cmap: Colormap = CLOUD_COVER
): [number, number, number, number] {
  const idx = valueToLutIndex(value, cmap)
  return [lut[idx * 4], lut[idx * 4 + 1], lut[idx * 4 + 2], lut[idx * 4 + 3]]
}
